import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { milestoneApi } from '../services/api';
import { ETAPPIO_COMPANY } from '../constants/company';
import type {
  Contract,
  ContractReplacement,
  Milestone,
  MilestoneBankIdSessionResponse,
  MilestoneMessage,
  MilestoneStartDecision
} from '../types';
import { canAttestMilestone } from '../utils/milestoneAttestation';
import AttestationCase from './AttestationCase';
import ConflictFlowIllustration from './ConflictFlowIllustration';
import ContractReplacementNotice from './ContractReplacementNotice';
import DisputeCooldownPanel from './DisputeCooldownPanel';
import ObjectionCase, { primaryClass, type ObjectionAction, type ObjectionAta, type ObjectionNextStep, type ObjectionStage } from './ObjectionCase';
import { stageVariationState } from './StageVariationNotice';
import { agreementVariationNumber } from '../utils/agreementIdentity';
import { needsPersonMilestoneApproval } from '../utils/personTodos';
import { variationOpenErrorText } from '../utils/variationBlock';
import { apiErrorText, knownApiErrorText, messageSendErrorText } from '../utils/apiError';
import { attestationRequestPendingText, formatMessageTime } from '../utils/messageThreads';

export type AttestationOutcome = { payout?: { status: 'blocked'; code: string }; refreshFailed?: boolean };

interface MilestoneAttestationModalProps {
  milestone: Milestone;
  /** Number of the contract the stage belongs to, for the objection header. */
  contractNumber?: number;
  contractMilestones?: Milestone[];
  messages: MilestoneMessage[];
  userRole: 'company' | 'person';
  onAttest: (
    action: 'approved' | 'rejected',
    bankidVerificationRef?: string,
    message?: string,
    milestoneStartDecisions?: MilestoneStartDecision[],
    /** Files with the approval or the objection (ClickUp 869fj6n1d, 869fmfeah). */
    files?: File[]
  ) => Promise<void | AttestationOutcome>;
  onPostMessage: (message: string, options?: { context?: 'attestation_question' | 'attestation_request'; files?: File[] }) => Promise<void>;
  onProgress?: (
    action: 'start_dispute_resolution' | 'resubmit_for_attestation',
    comment?: string
  ) => Promise<void | boolean>;
  onStartConflict?: () => Promise<void>;
  onStartDispute?: (verificationRef: string) => Promise<void>;
  onDownloadDisputeExport?: () => Promise<void>;
  onOpenMarketplace?: () => void;
  onCreateVariation?: (reason: string) => Promise<void | boolean>;
  onCreateVariationWithReference?: (message: MilestoneMessage) => Promise<void>;
  replacementContract?: Contract | null;
  replacementOriginalContract?: Contract | null;
  replacementSettlement?: ContractReplacement | null;
  onSignReplacement?: () => void;
  /** Opens the ÄTA for reading (the company, while it awaits the buyer). */
  onViewReplacement?: () => void;
  onApproveReplacementProposal?: () => void;
  onRejectReplacementProposal?: () => void;
  onWithdrawReplacementProposal?: () => void;
  isUpdatingReplacementProposal?: boolean;
  isOpeningReplacementSigning?: boolean;
  initialMode?: 'attestation' | 'resolution';
  /** The customer has not read the company's previous attestation request and
   * it is under 24 hours old (ClickUp 869fm3fy8, 869fmfkyz). */
  attestationRequestPending?: boolean;
  /** When that request was sent, and when a new one may be sent. */
  attestationRequestLatestAt?: string | null;
  attestationRequestNextAllowedAt?: string | null;
  /** Opened from a notification or e-mail: show the latest message (869fnf0e5). */
  revealLatestMessage?: boolean;
  onClose: () => void;
}

export default function MilestoneAttestationModal({
  milestone,
  contractNumber,
  messages,
  userRole,
  onAttest,
  onPostMessage,
  onProgress,
  onStartConflict,
  onStartDispute,
  onDownloadDisputeExport,
  onOpenMarketplace,
  onCreateVariation,
  onCreateVariationWithReference,
  replacementContract,
  replacementOriginalContract,
  replacementSettlement,
  onSignReplacement,
  onViewReplacement,
  onApproveReplacementProposal,
  onRejectReplacementProposal,
  onWithdrawReplacementProposal,
  isUpdatingReplacementProposal = false,
  isOpeningReplacementSigning = false,
  initialMode = 'attestation',
  attestationRequestPending = false,
  attestationRequestLatestAt = null,
  attestationRequestNextAllowedAt = null,
  revealLatestMessage = false,
  onClose
}: MilestoneAttestationModalProps) {
  const { t, i18n } = useTranslation();
  const [messageText, setMessageText] = useState('');
  // Files sent with the next message (ClickUp 869fj6n1d).
  const [messageFiles, setMessageFiles] = useState<File[]>([]);
  // "Be om attest" (ClickUp 869fm3fy8): the company asks the customer to attest.
  const [requestAttestation, setRequestAttestation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpeningReference, setIsOpeningReference] = useState(false);
  const [pendingAction, setPendingAction] = useState<null | 'approved' | 'rejected'>(null);
  const [bankIdStatus, setBankIdStatus] = useState<
    'idle' | 'starting' | 'waiting' | 'approved' | 'success' | 'cancelled' | 'failed'
  >('idle');
  const [bankIdSession, setBankIdSession] = useState<MilestoneBankIdSessionResponse | null>(null);
  const [bankIdQrImage, setBankIdQrImage] = useState<string | null>(null);
  const [attestationWarning, setAttestationWarning] = useState<string | null>(null);
  const [bankIdError, setBankIdError] = useState<string | null>(null);
  const [progressError, setProgressError] = useState<string | null>(null);
  const [showResolutionGuide, setShowResolutionGuide] = useState(
    userRole === 'person' &&
    initialMode === 'resolution' &&
    !['disputed', 'conflict', 'external_dispute', 'under_resolution'].includes(
      milestone.deliveryStatus ?? ''
    )
  );
  const [escalationOpen, setEscalationOpen] = useState(
    milestone.deliveryStatus === 'conflict' || Boolean(milestone.conflictStartedAt)
  );
  const flowRunRef = useRef(0);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => () => {
    ++flowRunRef.current;
  }, []);

  const myAttestation = userRole === 'company' ? milestone.companyAttestation : milestone.personAttestation;
  const fullyApproved = milestone.companyAttestation === 'approved' && milestone.personAttestation === 'approved';
  const legacyWorkflow = !milestone.deliveryStatus;
  const isConflict = milestone.deliveryStatus === 'conflict';
  const isUnderResolution = milestone.deliveryStatus === 'under_resolution';
  const hasActiveConflict = Boolean(milestone.conflictStartedAt);
  const isPriceAdjustment = milestone.itemType === 'variation_price_adjustment';
  const isZeroValuePriceAdjustment = isPriceAdjustment && milestone.amount === 0;
  const variationSubject = isZeroValuePriceAdjustment
    ? t('milestoneAttestation.subjectVariationChange')
    : t('milestoneAttestation.subjectVariationPriceChange');
  const variationSubjectDefinite = isZeroValuePriceAdjustment
    ? t('milestoneAttestation.subjectVariationChangeDefinite')
    : t('milestoneAttestation.subjectVariationPriceChangeDefinite');
  const subjectTitle = isPriceAdjustment
    ? variationSubject
    : t('milestoneAttestation.subjectMilestoneTitle', { number: milestone.milestoneNumber });
  const subjectLower = isPriceAdjustment
    ? t('milestoneAttestation.subjectVariationLower')
    : t('milestoneAttestation.subjectMilestoneLower');
  const subjectDefinite = isPriceAdjustment
    ? variationSubjectDefinite
    : t('milestoneAttestation.subjectMilestoneDefinite');
  const approvalSubject = isPriceAdjustment
    ? variationSubjectDefinite
    : t('milestoneAttestation.subjectMilestoneNumber', { number: milestone.milestoneNumber });
  const subjectApproved = isPriceAdjustment
    ? t('milestoneAttestation.subjectApprovedVariation', { subject: variationSubjectDefinite })
    : t('milestoneAttestation.subjectApprovedMilestone');
  const subjectUnchanged = isPriceAdjustment
    ? t('milestoneAttestation.subjectUnchangedVariation', { subject: variationSubjectDefinite })
    : t('milestoneAttestation.subjectUnchangedMilestone');
  const subjectDisputed = isPriceAdjustment
    ? variationSubjectDefinite
    : t('milestoneAttestation.subjectDisputedMilestone');
  const supportEmailSubject = t('milestoneAttestation.supportEmailSubject', {
    subject: subjectTitle,
    id: milestone.id
  });
  const canFormallyDispute = messageText.trim().length > 0;
  const isExternalDispute = milestone.deliveryStatus === 'external_dispute';
  const isDisputed = ['disputed', 'conflict'].includes(
    milestone.deliveryStatus ?? ''
  );
  const isUnderResolutionConflictView =
    isUnderResolution && initialMode === 'resolution';
  const isActiveConflictResolutionView =
    hasActiveConflict && initialMode === 'resolution';
  const isOngoingWorkConflictView =
    ['in_progress', 'ready_for_attestation', 'seller_attested'].includes(milestone.deliveryStatus ?? '') &&
    !fullyApproved && initialMode === 'resolution';
  const isResolutionOpen =
    isDisputed ||
    isExternalDispute ||
    isUnderResolutionConflictView ||
    isActiveConflictResolutionView;
  const canWithdrawDispute = (isDisputed || isUnderResolution) && userRole === 'person' &&
    milestone.companyAttestation === 'approved' && milestone.personAttestation === 'rejected';
  const canResolveActiveConflict = hasActiveConflict && userRole === 'person' &&
    milestone.companyAttestation === 'approved';
  const showDisputeResolutionCopy =
    canResolveActiveConflict || (canWithdrawDispute && !isUnderResolution);
  const workflowAllowsAttestation = canAttestMilestone(milestone, userRole);
  const canApprove = !fullyApproved && (
    canWithdrawDispute || legacyWorkflow ||
    workflowAllowsAttestation
  );
  const canReject = !fullyApproved && !isDisputed && !isUnderResolution && userRole === 'person' && (
    legacyWorkflow || workflowAllowsAttestation
  );
  const canAttest =
    (canApprove || canReject) &&
    !isUnderResolutionConflictView &&
    !isActiveConflictResolutionView &&
    !isOngoingWorkConflictView;
  // The message box serves both "send only" and "approve with a message":
  // the buttons say which one happens (ClickUp 869fgj68c).
  const offersApproval = canAttest && canApprove && !isResolutionOpen && !showResolutionGuide;
  const canRequestAttestation = userRole === 'company' && needsPersonMilestoneApproval(milestone) && !attestationRequestPending;
  const requestAttestationBlocked = userRole === 'company' && needsPersonMilestoneApproval(milestone) && attestationRequestPending
    ? attestationRequestPendingText(t, i18n.language, {
      milestoneNumber: milestone.milestoneNumber,
      latestRequestAt: attestationRequestLatestAt,
      nextRequestAllowedAt: attestationRequestNextAllowedAt,
    })
    : undefined;
  const requestAttestationOption = canRequestAttestation ? {
    checked: requestAttestation,
    onChange: (checked: boolean) => {
      setRequestAttestation(checked);
      if (checked && !messageText.trim()) setMessageText(t('messageThreads.attestationRequest.prefill', { number: milestone.milestoneNumber }));
    },
    label: t('messageThreads.attestationRequest.label', { number: milestone.milestoneNumber }),
    hint: t('messageThreads.attestationRequest.hint'),
  } : undefined;
  const approvalMessage = messageText.trim();
  const showAttestationHint = myAttestation === 'approved'
    ? canAttest && canReject
    : myAttestation === 'rejected' && (isDisputed || (canAttest && canApprove));

  // The objection case gets its own structure: where it stands, one next
  // step, the objection and the money, the thread and escalation folded away
  // (ClickUp 869fh5n6z). Ordinary attestation keeps its layout.
  // "Attestera etappmål" stays the ordinary attestation, separate from
  // conflict management: a remedied stage only gets the case view when it is
  // opened from conflict management, and a new attestation after the remedy
  // is ordinary attestation.
  const resolutionView = initialMode === 'resolution';
  const objectionStage: ObjectionStage | null = fullyApproved
    ? null
    : isConflict || (hasActiveConflict && (resolutionView || isDisputed))
      ? 'conflict'
      : milestone.deliveryStatus === 'disputed'
        ? 'disputed'
        : isUnderResolution && resolutionView
          ? 'under_resolution'
          : null;
  const caseView = objectionStage !== null && !showResolutionGuide;
  const ata: ObjectionAta | null = (() => {
    if (!replacementContract || !replacementSettlement?.amendmentScope) return null;
    const state = stageVariationState(replacementContract, replacementSettlement);
    if (state !== 'draft' && state !== 'signing') return null;
    const proposal = replacementSettlement.contract ?? replacementContract;
    // Signed by both and awaiting activation: no longer waiting for anyone.
    if (state === 'signing' && (proposal.personSigned || replacementContract.personSigned)) return null;
    return {
      number: agreementVariationNumber(proposal) ?? Math.max((proposal.contractVersion ?? 2) - 1, 1),
      state,
      reason: replacementSettlement.reason
    };
  })();

  useEffect(() => {
    const qrData = bankIdSession?.qrCode?.data;
    if (!qrData) {
      setBankIdQrImage(null);
      return;
    }
    let active = true;
    QRCode.toDataURL(qrData, { width: 192, margin: 1 })
      .then((image) => {
        if (active) setBankIdQrImage(image);
      })
      .catch(() => {
        if (active) setBankIdQrImage(null);
      });
    return () => {
      active = false;
    };
  }, [bankIdSession?.qrCode?.data]);

  const wait = (milliseconds: number) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const submitVerifiedAttestation = async (
    action: 'approved',
    verificationRef: string,
    message?: string,
    files: File[] = []
  ) => {
    setBankIdStatus('approved');
    try {
      const outcome = files.length > 0
        ? await onAttest(action, verificationRef, message, undefined, files)
        : await onAttest(action, verificationRef, message);
      setAttestationWarning(outcome?.payout?.status === 'blocked'
        ? t('milestoneAttestation.savedPayoutBlocked')
        : outcome?.refreshFailed ? t('milestoneAttestation.savedRefreshFailed') : null);
      setMessageText('');
      setMessageFiles([]);
      setBankIdStatus('success');
    } catch (error) {
      // The files stay selected, so "Försök igen" sends them again (ClickUp 869fmfeah).
      setBankIdError(apiErrorText(t, error, files.length > 0
        ? 'milestoneAttestation.filesUploadFailed'
        : 'milestoneAttestation.bankIdErrorSaveFailed'));
      setBankIdStatus('failed');
    }
  };

  const handleBankIdResult = async (
    nextSession: MilestoneBankIdSessionResponse,
    action: 'approved',
    message: string | undefined,
    files: File[],
    runId: number
  ) => {
    if (flowRunRef.current !== runId) return true;
    setBankIdSession(nextSession);
    if (nextSession.status === 'complete' || nextSession.verified) {
      await submitVerifiedAttestation(action, nextSession.verificationRef, message, files);
      return true;
    }
    if (nextSession.status === 'cancelled') {
      setBankIdStatus('cancelled');
      return true;
    }
    if (nextSession.status === 'failed' || nextSession.status === 'expired') {
      setBankIdError(nextSession.message || t('milestoneAttestation.bankIdVerificationFailed'));
      setBankIdStatus('failed');
      return true;
    }
    return false;
  };

  const startBankIdAttestation = async (action: 'approved') => {
    const runId = ++flowRunRef.current;
    const message = messageText.trim() || undefined;
    // Captured with the message, so the files signed for are the ones sent.
    const files = messageFiles;
    setBankIdStatus('starting');
    setBankIdError(null);
    setBankIdQrImage(null);
    try {
      let session = await milestoneApi.startAttestationBankId(milestone.id);
      if (flowRunRef.current !== runId) return;
      setBankIdSession(session);
      setBankIdStatus('waiting');
      if (await handleBankIdResult(session, action, message, files, runId)) return;

      while (flowRunRef.current === runId) {
        await wait(session.pollIntervalMs ?? 1500);
        if (flowRunRef.current !== runId) return;
        session = session.provider === 'simulated'
          ? await milestoneApi.collectAttestationBankId(milestone.id, session.verificationRef)
          : await milestoneApi.getAttestationBankIdStatus(milestone.id, session.verificationRef);
        if (await handleBankIdResult(session, action, message, files, runId)) return;
      }
    } catch {
      if (flowRunRef.current !== runId) return;
      setBankIdError(t('milestoneAttestation.bankIdVerificationCouldNotComplete'));
      setBankIdStatus('failed');
    }
  };

  const cancelBankIdAttestation = async (showCancelledStatus = true) => {
    ++flowRunRef.current;
    const verificationRef = bankIdSession?.verificationRef;
    if (verificationRef) {
      try {
        await milestoneApi.cancelAttestationBankId(milestone.id, verificationRef);
      } catch {
        // The local flow is still cancelled. A pending provider session expires
        // without ever being accepted by the attestation endpoint.
      }
    }
    if (showCancelledStatus) setBankIdStatus('cancelled');
  };

  const retryBankIdAttestation = () => {
    if (!pendingAction) return;
    if (pendingAction === 'rejected') {
      void submitDispute();
      return;
    }
    if (bankIdSession?.status === 'complete' || bankIdSession?.verified) {
      void submitVerifiedAttestation(
        pendingAction,
        bankIdSession.verificationRef,
        messageText.trim() || undefined,
        messageFiles
      );
      return;
    }
    void startBankIdAttestation(pendingAction);
  };

  const handleClose = () => {
    if (bankIdStatus === 'starting' || bankIdStatus === 'waiting') {
      void cancelBankIdAttestation(false);
    } else {
      ++flowRunRef.current;
    }
    onClose();
  };

  const bankIdBusy = ['starting', 'waiting', 'approved'].includes(bankIdStatus);

  const dismissBankIdStep = () => {
    if (bankIdStatus === 'starting' || bankIdStatus === 'waiting') {
      void cancelBankIdAttestation();
      return;
    }
    if (bankIdStatus === 'success') {
      handleClose();
      return;
    }
    ++flowRunRef.current;
    setPendingAction(null);
    setBankIdStatus('idle');
    setBankIdSession(null);
    setBankIdError(null);
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() && messageFiles.length === 0) return;
    setIsSubmitting(true);
    setProgressError(null);
    try {
      // Written instead of approving: labelled "Fråga vid attestering" on Meddelanden.
      await onPostMessage(messageText.trim(), {
        ...(canRequestAttestation && requestAttestation
          ? { context: 'attestation_request' as const }
          : offersApproval ? { context: 'attestation_question' as const } : {}),
        ...(messageFiles.length > 0 ? { files: messageFiles } : {})
      });
      setMessageText('');
      setMessageFiles([]);
      setRequestAttestation(false);
    } catch (error) {
      setProgressError(messageSendErrorText(t, error, 'counterproposal.messageFailed', iso => formatMessageTime(iso, i18n.language)));
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitDispute = async () => {
    const message = messageText.trim();
    if (!message) return;
    setBankIdStatus('approved');
    setBankIdError(null);
    try {
      if (messageFiles.length > 0) await onAttest('rejected', undefined, message, undefined, messageFiles);
      else await onAttest('rejected', undefined, message);
      setMessageText('');
      setMessageFiles([]);
      setBankIdStatus('success');
    } catch (error) {
      // The reason stays in the field, so "Försök igen" sends it again.
      const code = (error as { code?: string } | null)?.code;
      setBankIdError(code === 'ECONNABORTED' || code === 'ETIMEDOUT'
        ? t('milestoneAttestation.disputeTimedOut')
        : apiErrorText(t, error, 'milestoneAttestation.disputeSaveFailed'));
      setBankIdStatus('failed');
    }
  };

  const progressResolution = async (
    action: 'start_dispute_resolution' | 'resubmit_for_attestation'
  ) => {
    if (!onProgress) return;
    setIsSubmitting(true);
    setProgressError(null);
    try {
      if (await onProgress(action, messageText.trim() || undefined) === false) return;
      setMessageText('');
      if (action === 'start_dispute_resolution') onClose();
    } catch (error) {
      setProgressError(apiErrorText(t, error, 'milestoneAttestation.progressStatusUpdateFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };


  const createVariation = async (reference?: MilestoneMessage) => {
    if (isSubmitting || isOpeningReference) return;
    setIsOpeningReference(true);
    setProgressError(null);
    try {
      if (reference && onCreateVariationWithReference) await onCreateVariationWithReference(reference);
      else if (onCreateVariation && await onCreateVariation(messageText.trim()) === false) return;
    } catch (failure) {
      // An unfinished ÄTA on the contract says which one (ClickUp 869fkk2jh).
      setProgressError(variationOpenErrorText(failure, knownApiErrorText(t, failure) ?? t('conflictVariation.openFailed')));
    } finally {
      setIsOpeningReference(false);
    }
  };


  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('sv-SE').format(amount);

  const busy = isSubmitting || bankIdBusy || isOpeningReference;
  const openEscalation = () => {
    setEscalationOpen(true);
    window.setTimeout(() => document.querySelector('[data-testid="objection-escalation"]')?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 0);
  };
  const askAboutAta = (number: number) => {
    if (!messageText.trim()) setMessageText(t('objectionCase.thread.aboutAtaPrefix', { number }));
    messageInputRef.current?.focus();
  };
  // The buyer may still approve: withdraw the objection, accept the remedy
  // or end the conflict. Only offered when the workflow allows it.
  const withdrawLink = (): ObjectionNextStep['link'] => {
    const label = canResolveActiveConflict
      ? 'objectionCase.next.resolveConflictLink'
      : canAttest && canApprove
        ? canWithdrawDispute && !isUnderResolution ? 'objectionCase.next.withdrawLink' : 'objectionCase.next.acceptRemedyLink'
        : null;
    return label
      ? { prompt: t('objectionCase.next.withdrawPrompt'), label: t(label), onClick: () => setPendingAction('approved'), disabled: busy, testId: 'milestone-approve' }
      : undefined;
  };
  const remedyAction: ObjectionAction | null = userRole === 'company' && onProgress && ['disputed', 'conflict'].includes(milestone.deliveryStatus ?? '')
    ? { label: isSubmitting ? t('milestoneAttestation.saving') : t('objectionCase.next.remedy'),
        onClick: () => void progressResolution('start_dispute_resolution'), disabled: busy, testId: 'objection-remedy' }
    : null;
  const proposeAtaAction: ObjectionAction | null = userRole === 'company' && onCreateVariation
    ? { label: t(isOpeningReference ? 'conflictVariation.opening' : 'objectionCase.next.proposeAta'),
        onClick: () => void createVariation(), disabled: busy, testId: 'create-conflict-variation' }
    : null;
  let alternative: { title: string; text: string; action: ObjectionAction } | undefined;
  const nextStep: ObjectionNextStep = (() => {
    if (userRole === 'person') {
      if (ata?.state === 'signing' && (objectionStage === 'disputed' || objectionStage === 'conflict') && onSignReplacement) {
        return {
          kind: 'action',
          title: t('objectionCase.next.personSignAta.title', { number: ata.number }),
          text: t('objectionCase.next.personSignAta.text'),
          primary: { label: t(isOpeningReplacementSigning ? 'contractReplacement.openingSigning' : 'objectionCase.next.signAta', { number: ata.number }),
            onClick: onSignReplacement, disabled: busy || isOpeningReplacementSigning, testId: 'objection-sign-ata', tone: 'cta' },
          secondary: { label: t('objectionCase.next.askAboutAta'), onClick: () => askAboutAta(ata.number), disabled: busy, testId: 'objection-ask-about-ata' },
          link: withdrawLink()
        };
      }
      if (objectionStage === 'conflict') {
        return {
          kind: 'action',
          title: t('objectionCase.next.conflict.title'),
          text: t('objectionCase.next.conflict.personText'),
          primary: onOpenMarketplace
            ? { label: t('objectionCase.next.getHelp'), onClick: onOpenMarketplace, disabled: busy, testId: 'objection-get-help', tone: 'cta' }
            : undefined,
          secondary: onStartConflict
            ? { label: t('objectionCase.next.startDispute'), onClick: openEscalation, disabled: busy, testId: 'objection-open-escalation' }
            : undefined,
          link: withdrawLink()
        };
      }
      if (objectionStage === 'under_resolution') {
        return { kind: 'wait', title: t('objectionCase.next.personRemedy.title'), text: t('objectionCase.next.personRemedy.text'), link: withdrawLink() };
      }
      return { kind: 'wait', title: t('objectionCase.next.personWaitCompany.title'), text: t('objectionCase.next.personWaitCompany.text'), link: withdrawLink() };
    }

    if (ata && (objectionStage === 'disputed' || objectionStage === 'conflict')) {
      if (remedyAction) {
        alternative = { title: t('objectionCase.next.companyRemedyInstead.title'), text: t('objectionCase.next.companyRemedyInstead.text'), action: remedyAction };
      }
      if (ata.state === 'signing') {
        return {
          kind: 'wait',
          title: t('objectionCase.next.companyWaitSignature.title', { number: ata.number }),
          text: t('objectionCase.next.companyWaitSignature.text'),
          // Opens the ÄTA in its wizard, which shows the signing state and lets
          // the company withdraw the sending to edit it.
          secondary: proposeAtaAction
            ? { ...proposeAtaAction, label: t(isOpeningReference ? 'conflictVariation.opening' : 'objectionCase.next.viewAta', { number: ata.number }) }
            : onViewReplacement
              ? { label: t('objectionCase.next.viewAta', { number: ata.number }), onClick: onViewReplacement, disabled: busy, testId: 'objection-view-ata' }
              : undefined
        };
      }
      return {
        kind: 'action',
        title: t('objectionCase.next.companyAtaDraft.title', { number: ata.number }),
        text: t('objectionCase.next.companyAtaDraft.text'),
        primary: proposeAtaAction
          ? { ...proposeAtaAction, label: t(isOpeningReference ? 'conflictVariation.opening' : 'objectionCase.next.continueAta', { number: ata.number }), tone: 'navy' }
          : undefined
      };
    }
    if (objectionStage === 'conflict') {
      if (remedyAction) {
        alternative = { title: t('objectionCase.next.companyRemedyInstead.title'), text: t('objectionCase.next.companyRemedyInstead.text'), action: remedyAction };
      }
      return {
        kind: 'action',
        title: t('objectionCase.next.conflict.title'),
        text: t('objectionCase.next.conflict.companyText'),
        primary: onOpenMarketplace
          ? { label: t('objectionCase.next.getHelp'), onClick: onOpenMarketplace, disabled: busy, testId: 'objection-get-help', tone: 'navy' }
          : undefined,
        secondary: proposeAtaAction ?? undefined
      };
    }
    if (objectionStage === 'disputed') {
      return {
        kind: 'action',
        title: t('objectionCase.next.companyRespond.title'),
        text: t('objectionCase.next.companyRespond.text'),
        primary: proposeAtaAction ? { ...proposeAtaAction, tone: 'navy' } : remedyAction ? { ...remedyAction, tone: 'navy' } : undefined,
        secondary: proposeAtaAction && remedyAction ? remedyAction : undefined
      };
    }
    if (objectionStage === 'under_resolution') {
      return {
        kind: 'action',
        title: t('objectionCase.next.companyUnderResolution.title'),
        text: t('objectionCase.next.companyUnderResolution.text'),
        primary: onProgress
          ? { label: isSubmitting ? t('milestoneAttestation.saving') : t('objectionCase.next.resubmit'),
              onClick: () => void progressResolution('resubmit_for_attestation'), disabled: busy, testId: 'objection-resubmit', tone: 'navy' }
          : undefined,
        secondary: proposeAtaAction ?? undefined
      };
    }
    return { kind: 'wait', title: t('objectionCase.next.companyWaitAttest.title'), text: t('objectionCase.next.companyWaitAttest.text') };
  })();
  const objectionStatusKey = objectionStage === 'under_resolution'
    ? 'underResolution'
    : objectionStage ?? 'disputed';

  // Ordinary attestation in the same design as the objection case: one next
  // step, the money and the dialogue (ClickUp 869fkhmbb).
  const escalationPanel = onStartConflict && !fullyApproved ? (
    <DisputeCooldownPanel
      milestone={milestone}
      onStartConflict={onStartConflict}
      onStartDispute={onStartDispute}
      onDownloadExport={onDownloadDisputeExport}
      canStartDispute={userRole === 'person'}
      viewerSide={userRole}
      onOpenMarketplace={onOpenMarketplace}
    />
  ) : null;
  // "Problem under pågående arbete?" sits folded under the attestation's
  // buttons, so attestation stays the obvious path; it is open when the stage
  // was opened from conflict management.
  const showOngoingWorkEscalation = Boolean(escalationPanel) && !caseView && !isResolutionOpen &&
    ['in_progress', 'ready_for_attestation', 'seller_attested', 'ready_for_reattestation'].includes(milestone.deliveryStatus ?? '');
  const contractReference = contractNumber
    ? t('attestationCase.bankId.contractNumber', { number: contractNumber })
    : t('attestationCase.bankId.theContract');
  const companyAttested = milestone.companyAttestation === 'approved';
  const personAttested = milestone.personAttestation === 'approved';
  const attestationNextStep: ObjectionNextStep = (() => {
    if (showResolutionGuide) {
      return { kind: 'action', title: t('milestoneAttestation.tryResolveBeforeDispute'), text: t('milestoneAttestation.contactSupportHint') };
    }
    if (fullyApproved) {
      return { kind: 'wait', title: t('attestationCase.next.approvedTitle'),
        text: t('attestationCase.next.approvedText', { subject: subjectApproved }) };
    }
    if (isResolutionOpen || isUnderResolution) {
      return {
        kind: canAttest && canApprove ? 'action' : 'wait',
        title: isUnderResolution
          ? t('milestoneAttestation.resolutionUnderResolutionBanner', { subject: subjectDisputed })
          : isConflict || hasActiveConflict
            ? t('milestoneAttestation.resolutionConflictBanner', { subject: subjectDisputed })
            : t('milestoneAttestation.resolutionDisputedBanner', { subject: subjectDisputed }),
        text: isUnderResolution
          ? userRole === 'company'
            ? t('milestoneAttestation.resolutionCompanyRemedyHint', { subject: subjectLower })
            : t('milestoneAttestation.resolutionPersonRemedyHint', { subject: subjectLower })
          : t('milestoneAttestation.resolutionDialogHint', { subject: subjectLower })
      };
    }
    if (canAttest && canApprove) {
      return userRole === 'person'
        ? { kind: 'action', title: t('attestationCase.next.personApprove.title', { subject: approvalSubject }),
            text: t(isPriceAdjustment ? 'attestationCase.next.personApprove.textVariation' : 'attestationCase.next.personApprove.text', { subject: approvalSubject }) }
        : { kind: 'action', title: t('attestationCase.next.companyApprove.title', { subject: approvalSubject }),
            text: t('attestationCase.next.companyApprove.text', { subject: subjectLower }) };
    }
    // Opened from conflict management while the work is ongoing: the escalation
    // panel below says what can be done, so no workflow hint here.
    if (isOngoingWorkConflictView) {
      return { kind: 'wait', title: t('attestationCase.next.ongoingWork.title'), text: t('attestationCase.next.ongoingWork.text') };
    }
    if (userRole === 'company' && companyAttested) {
      return { kind: 'wait', title: t('attestationCase.next.companyWait.title', { subject: subjectLower }),
        text: t('attestationCase.next.companyWait.text', { subject: approvalSubject }) };
    }
    if (userRole === 'person' && personAttested) {
      return { kind: 'wait', title: t('attestationCase.next.personWait.title'),
        text: t('attestationCase.next.personWait.text', { subject: approvalSubject }) };
    }
    return {
      kind: 'wait',
      title: t(isPriceAdjustment ? 'attestationCase.next.notReady.titleVariation' : 'attestationCase.next.notReady.title'),
      text: isPriceAdjustment
        ? t('milestoneAttestation.variationMustBeReady', { subject: variationSubjectDefinite })
        : t('milestoneAttestation.milestoneMustFollowWorkflow')
    };
  })();
  const attestationStatus: { key: string; tone: string; icon: string } = showResolutionGuide
    ? { key: 'attestationCase.status.somethingWrong', tone: 'disputed', icon: 'help-circle' }
    : fullyApproved
      ? { key: 'attestationCase.status.approved', tone: 'approved', icon: 'check-circle' }
      : isConflict || isActiveConflictResolutionView
        ? { key: 'objectionCase.status.conflict', tone: 'conflict', icon: 'pause-circle' }
        : isUnderResolution
          ? { key: 'objectionCase.status.underResolution', tone: 'underResolution', icon: 'pause-circle' }
          : isDisputed || isExternalDispute
            ? { key: 'objectionCase.status.disputed', tone: 'disputed', icon: 'pause-circle' }
            : canAttest && canApprove
              ? { key: 'attestationCase.status.awaitingYou', tone: 'attest', icon: 'edit-3' }
              : userRole === 'company' && companyAttested
                ? { key: 'attestationCase.status.awaitingPerson', tone: 'waiting', icon: 'clock' }
                : userRole === 'person' && personAttested
                  ? { key: 'attestationCase.status.awaitingCompany', tone: 'waiting', icon: 'clock' }
                  : { key: 'attestationCase.status.inProgress', tone: 'waiting', icon: 'clock' };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, overflow: 'auto' }} onClick={handleClose}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable modal-fullscreen-sm-down objection-dialog" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header objection-header">
            <div className="objection-header-main">
              <div className="objection-kicker">
                {contractNumber
                  ? t('objectionCase.header.kicker', { contract: contractNumber, subject: subjectTitle })
                  : subjectTitle}
              </div>
              <div className="objection-header-title">
                <h2 className="modal-title">{milestone.description}</h2>
                <span className="objection-header-amount">{formatAmount(milestone.amount)} kr</span>
              </div>
              {caseView ? (
                <span className={`objection-status-pill is-${objectionStatusKey}`} data-testid="objection-status">
                  <i className="feather-pause-circle" aria-hidden="true"></i>
                  {t(`objectionCase.status.${objectionStatusKey}`)}
                </span>
              ) : (
                <span className={`objection-status-pill is-${attestationStatus.tone}`} data-testid="attestation-status">
                  <i className={`feather-${attestationStatus.icon}`} aria-hidden="true"></i>
                  {t(attestationStatus.key)}
                </span>
              )}
            </div>
            <button type="button" className="btn-close" onClick={handleClose} aria-label={t('milestoneAttestation.close')}></button>
          </div>

          <div className="modal-body">
            {caseView && objectionStage ? (
              <ObjectionCase
                milestone={milestone}
                stage={objectionStage}
                hasAta={Boolean(ata) || Boolean(replacementSettlement?.amendmentScope)}
                userRole={userRole}
                messages={messages}
                nextStep={nextStep}
                alternative={alternative}
                notices={replacementContract && replacementSettlement && !ata ? (
                  <ContractReplacementNotice
                    contract={replacementContract}
                    originalContract={replacementOriginalContract}
                    settlement={replacementSettlement}
                    compact
                    onSign={userRole === 'person' ? onSignReplacement : undefined}
                    isSigning={isOpeningReplacementSigning}
                    onApproveProposal={userRole === 'company' ? onApproveReplacementProposal : undefined}
                    onRejectProposal={userRole === 'company' ? onRejectReplacementProposal : undefined}
                    onWithdrawProposal={userRole === 'person' ? onWithdrawReplacementProposal : undefined}
                    isUpdatingProposal={isUpdatingReplacementProposal}
                  />
                ) : null}
                escalation={onStartConflict ? (
                  <DisputeCooldownPanel
                    milestone={milestone}
                    onStartConflict={onStartConflict}
                    onStartDispute={onStartDispute}
                    onDownloadExport={onDownloadDisputeExport}
                    canStartDispute={userRole === 'person'}
                    viewerSide={userRole}
                    onOpenMarketplace={onOpenMarketplace}
                  />
                ) : null}
                escalationOpen={escalationOpen}
                onReferenceMessage={userRole === 'company' && onCreateVariationWithReference
                  ? message => void createVariation(message)
                  : undefined}
                referenceBusy={isOpeningReference}
                composer={{
                  value: messageText,
                  onChange: setMessageText,
                  onSend: () => void handleSendMessage(),
                  disabled: isSubmitting || bankIdBusy,
                  sendDisabled: isSubmitting || (!messageText.trim() && messageFiles.length === 0),
                  files: messageFiles,
                  onFilesChange: setMessageFiles,
                  requestAttestation: requestAttestationOption,
                  requestAttestationBlocked,
                  sending: isSubmitting,
                  inputRef: messageInputRef
                }}
                error={progressError}
              />
            ) : (
              <AttestationCase
                milestone={milestone}
                userRole={userRole}
                messages={messages}
                revealLatestMessage={revealLatestMessage}
                nextStep={attestationNextStep}
                nextStepKicker={showResolutionGuide ? t('attestationCase.guideKicker') : undefined}
                nextStepExtra={showResolutionGuide && !isResolutionOpen ? (
                  <div>
                    <a
                      className="btn btn-sm btn-outline-primary"
                      href={`mailto:${ETAPPIO_COMPANY.email}?subject=${encodeURIComponent(supportEmailSubject)}`}
                    >
                      <i className="feather-life-buoy me-1" aria-hidden="true"></i>{t('milestoneAttestation.contactSupportButton')}
                    </a>
                  </div>
                ) : null}
                notices={<>
                  {isResolutionOpen && milestone.disputeReason && (
                    <div className="objection-quote" data-testid="objection-reason">
                      <div className="objection-quote-label">{t('milestoneAttestation.disputeReasonLabel')}</div>
                      <blockquote className="mb-0">”{milestone.disputeReason}”</blockquote>
                    </div>
                  )}
                  {isResolutionOpen && (
                    <ConflictFlowIllustration
                      currentStep={isExternalDispute ? 'external' : 'locked'}
                      viewerSide={userRole}
                    />
                  )}
                  {replacementContract && replacementSettlement && (
                    <ContractReplacementNotice
                      contract={replacementContract}
                      originalContract={replacementOriginalContract}
                      settlement={replacementSettlement}
                      compact
                      onSign={userRole === 'person' ? onSignReplacement : undefined}
                      isSigning={isOpeningReplacementSigning}
                      onApproveProposal={userRole === 'company' ? onApproveReplacementProposal : undefined}
                      onRejectProposal={userRole === 'company' ? onRejectReplacementProposal : undefined}
                      onWithdrawProposal={userRole === 'person' ? onWithdrawReplacementProposal : undefined}
                      isUpdatingProposal={isUpdatingReplacementProposal}
                    />
                  )}
                  {/* The buyer answers in the dialogue; the company drives any change to
                      the agreement (ÄTA) from its side (ClickUp 869fgj7ck). */}
                  {isResolutionOpen && userRole === 'person' && (
                    <div className="small text-muted">{t('conflictVariation.personHint')}</div>
                  )}
                  {isResolutionOpen && userRole === 'company' && onCreateVariation && (
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <button type="button" className="btn btn-sm btn-outline-primary"
                        onClick={() => void createVariation()}
                        disabled={busy}
                        data-testid="create-conflict-variation">
                        <i className="feather-file-plus me-1" aria-hidden="true" />
                        {t(isOpeningReference ? 'conflictVariation.opening' : 'conflictVariation.create')}
                      </button>
                      <span className="small text-muted">{t('conflictVariation.companyHint')}</span>
                    </div>
                  )}
                  {userRole === 'company' && onProgress && ['disputed', 'conflict'].includes(milestone.deliveryStatus ?? '') && (
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => void progressResolution('start_dispute_resolution')}
                        disabled={isSubmitting || bankIdBusy}
                      >
                        <i className="feather-tool me-1"></i>
                        {isSubmitting ? t('milestoneAttestation.saving') : t('milestoneAttestation.weRemedyProblem')}
                      </button>
                      <span className="small text-muted">{t('milestoneAttestation.disputeRemainsWhileRemedy')}</span>
                    </div>
                  )}
                  {isResolutionOpen && escalationPanel}
                  {showAttestationHint && !fullyApproved && (
                    <div className="small text-muted">
                      <i className="feather-info me-1"></i>
                      {myAttestation === 'approved'
                        ? t('milestoneAttestation.hintApproved', { subject: subjectDefinite })
                        : isDisputed
                          ? t('milestoneAttestation.hintDisputed', { subject: subjectLower })
                          : t('milestoneAttestation.hintRejected', { subject: subjectDefinite })}
                    </div>
                  )}
                </>}
                onReferenceMessage={userRole === 'company' && (isResolutionOpen || isUnderResolution) && onCreateVariationWithReference
                  ? message => void createVariation(message)
                  : undefined}
                referenceBusy={isOpeningReference}
                composer={{
                  value: messageText,
                  onChange: setMessageText,
                  onSend: () => void handleSendMessage(),
                  disabled: isSubmitting || bankIdBusy,
                  sendDisabled: isSubmitting || (!messageText.trim() && messageFiles.length === 0),
                  files: messageFiles,
                  onFilesChange: setMessageFiles,
                  requestAttestation: requestAttestationOption,
                  requestAttestationBlocked,
                  sending: isSubmitting,
                  inputRef: messageInputRef,
                  label: showResolutionGuide
                    ? t('milestoneAttestation.whatIsWrongLabel')
                    : isResolutionOpen ? t('milestoneAttestation.replyToCounterpartyLabel') : undefined,
                  placeholder: showResolutionGuide
                    ? t('milestoneAttestation.placeholderDescribeIssue')
                    : isResolutionOpen ? t('milestoneAttestation.placeholderProposalOrStep') : undefined,
                  submitLabel: isResolutionOpen
                    ? t('milestoneAttestation.sendReply')
                    : showResolutionGuide
                      ? t('milestoneAttestation.sendToCompany')
                      : offersApproval ? t('milestoneAttestation.sendMessageWithoutApproving') : undefined,
                  hint: offersApproval ? t('attestationCase.composeHint') : undefined
                }}
                error={progressError}
              />
            )}
          </div>

          {/* Phone: the one primary action stays at the bottom of the sheet. */}
          {caseView && nextStep.primary && (
            <div className="modal-footer objection-mobile-footer d-sm-none">
              <button type="button" className={`btn ${primaryClass(nextStep.primary.tone)} w-100`}
                onClick={nextStep.primary.onClick} disabled={nextStep.primary.disabled}
                data-testid={nextStep.primary.testId ? `${nextStep.primary.testId}-mobile` : undefined}>
                {nextStep.primary.label}
              </button>
            </div>
          )}

          {/* Footer with attestation buttons — visible until the milestone is
              fully approved by both parties. */}
          {canAttest && !showResolutionGuide && !caseView && (
            <div className="modal-footer attestation-footer">
              {canReject ? (
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowResolutionGuide(true)}
                  disabled={isSubmitting || bankIdBusy}
                >
                  <i className="feather-help-circle me-1"></i>
                  {t('milestoneAttestation.statusSomethingWrong')}
                </button>
              ) : <span />}
              {canApprove && (
                <div className="attestation-footer-main">
                  <small className="attestation-footer-hint">{t('attestationCase.bankIdNextStep')}</small>
                  <button
                    className="btn btn-success"
                    onClick={() => setPendingAction('approved')}
                    disabled={isSubmitting || bankIdBusy}
                    data-testid="milestone-approve"
                  >
                    <i className="feather-check-circle me-1" aria-hidden="true"></i>
                    {showDisputeResolutionCopy
                      ? isConflict || hasActiveConflict
                        ? t('milestoneAttestation.approveAndResolveConflict', { subject: subjectLower })
                        : t('milestoneAttestation.withdrawDisputeAndApprove')
                      : approvalMessage
                        ? t('milestoneAttestation.approveSubjectWithMessage', { subject: approvalSubject })
                        : t('milestoneAttestation.approveSubject', { subject: approvalSubject })}
                  </button>
                </div>
              )}
            </div>
          )}
          {!canAttest && !showResolutionGuide && !caseView && (
            <div className="modal-footer">
              <button type="button" className="btn btn-light ms-auto" onClick={handleClose} data-testid="attestation-close">
                {t('milestoneAttestation.close')}
              </button>
            </div>
          )}
          {showResolutionGuide && canReject && (
            <div className="modal-footer attestation-footer">
              <button
                className="btn btn-light"
                onClick={() => setShowResolutionGuide(false)}
                disabled={isSubmitting || bankIdBusy}
              >
                {t('milestoneAttestation.back')}
              </button>
              <div className="attestation-footer-main">
                <small className="attestation-footer-hint">{t('milestoneAttestation.disputeReasonNextStep')}</small>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setPendingAction('rejected')}
                  disabled={isSubmitting || bankIdBusy}
                  data-testid="open-dispute-confirmation"
                >
                  <i className="feather-message-circle me-1"></i>{t('milestoneAttestation.objectWithReason')}
                </button>
              </div>
            </div>
          )}
          {showOngoingWorkEscalation && (
            <details className="objection-escalation attestation-escalation" open={isOngoingWorkConflictView}
              data-testid="attestation-escalation">
              <summary>
                <i className="feather-life-buoy objection-escalation-icon" aria-hidden="true"></i>
                <span className="objection-escalation-text">
                  <span className="objection-escalation-title">{t('disputeCooldown.ongoingWorkTitle')}</span>
                </span>
                <i className="feather-chevron-down objection-escalation-chevron" aria-hidden="true"></i>
              </summary>
              <div className="objection-escalation-body">{escalationPanel}</div>
            </details>
          )}
        </div>
      </div>

      {/* Confirmation step before an attestation is submitted */}
      {pendingAction && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="milestone-bankid-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable objection-dialog" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header objection-header">
                <div className="objection-header-main">
                  <div className="objection-kicker">{subjectTitle} · {formatAmount(milestone.amount)} kr</div>
                  <div className="objection-header-title">
                    <h2 className="modal-title" id="milestone-bankid-title">
                      {pendingAction === 'rejected'
                        ? t('milestoneAttestation.confirmDispute')
                        : showDisputeResolutionCopy
                          ? t('milestoneAttestation.bankIdAtAttestation')
                          : t('attestationCase.bankId.title')}
                    </h2>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={dismissBankIdStep}
                  disabled={bankIdStatus === 'approved'}
                  aria-label={t('milestoneAttestation.close')}
                ></button>
              </div>
              <div className="modal-body">
                {bankIdStatus === 'idle' && (
                  <>
                    {pendingAction === 'rejected' || showDisputeResolutionCopy ? (
                      <p>
                        {pendingAction === 'rejected'
                          ? t('milestoneAttestation.disputeSavedDirectly')
                          : isConflict || hasActiveConflict
                            ? t('milestoneAttestation.confirmBankIdApproveConflict', { subject: subjectLower })
                            : t('milestoneAttestation.confirmBankIdWithdrawDispute', { subject: subjectLower })}
                      </p>
                    ) : (
                      // What the signature certifies (ClickUp 869fkhmbb). The legal
                      // wording is settled in its own story and left out for now.
                      <section className="attestation-certify mb-3"aria-labelledby="attestation-certify-title" data-testid="attestation-certify">
                        <h3 id="attestation-certify-title" className="objection-section-title mb-1">{t('attestationCase.bankId.certifyTitle')}</h3>
                        <p className="mb-2">{t('attestationCase.bankId.certifyLead')}</p>
                        <ul className="attestation-certify-list">
                          <li>
                            {isPriceAdjustment
                              ? t('attestationCase.bankId.certifyVariation', { subject: approvalSubject, contract: contractReference })
                              : t('attestationCase.bankId.certifyWork', { subject: approvalSubject, description: milestone.description, contract: contractReference })}
                          </li>
                          {userRole === 'person' && milestone.amount > 0 && (
                            <li>{t('attestationCase.bankId.certifyPayout', { amount: `${formatAmount(milestone.amount)} kr` })}</li>
                          )}
                        </ul>
                      </section>
                    )}
                    {pendingAction === 'rejected' ? (
                      <div className="mb-0">
                        <label
                          className="form-label small fw-semibold"
                          htmlFor="milestone-dispute-reason"
                        >
                          {t('milestoneAttestation.whatIsWrongLabel')}
                        </label>
                        <textarea
                          id="milestone-dispute-reason"
                          className="form-control"
                          rows={4}
                          placeholder={t('milestoneAttestation.placeholderDescribeIssue')}
                          value={messageText}
                          onChange={event => setMessageText(event.target.value)}
                          autoFocus
                          required
                          aria-required="true"
                          data-testid="dispute-confirmation-reason"
                        />
                        {!canFormallyDispute && (
                          <div className="small text-muted mt-2">
                            {t('milestoneAttestation.describeIssueToDispute')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {approvalMessage && (
                          <div className="mb-3" data-testid="approval-message-preview">
                            <div className="small fw-semibold">{t('milestoneAttestation.messageSentWithApproval')}</div>
                            <div className="small border rounded p-2 mt-1 text-break">“{approvalMessage}”</div>
                          </div>
                        )}
                        <div className="alert alert-info mb-0 small">
                          {t('milestoneAttestation.attestationSavedAfterBankId')}
                        </div>
                      </>
                    )}
                  </>
                )}

                {bankIdStatus === 'starting' && (
                  <div className="text-center py-4" role="status" aria-live="polite">
                    <div className="spinner-border text-primary mb-3" aria-hidden="true"></div>
                    <h6>{t('milestoneAttestation.startingBankId')}</h6>
                  </div>
                )}

                {bankIdStatus === 'waiting' && (
                  <div className="text-center py-2" role="status" aria-live="polite">
                    {bankIdQrImage ? (
                      <div className="d-inline-flex bg-white border rounded p-2 mb-3">
                        <img
                          src={bankIdQrImage}
                          alt={t('milestoneAttestation.qrCodeAlt')}
                          width="192"
                          height="192"
                        />
                      </div>
                    ) : (
                      <div className="spinner-border text-primary mb-3" aria-hidden="true"></div>
                    )}
                    <h6>{t('milestoneAttestation.waitingForBankId')}</h6>
                    <p className="text-muted mb-2">
                      {bankIdSession?.message || t('milestoneAttestation.openBankIdApp')}
                    </p>
                    {bankIdSession?.sameDeviceUrl && (
                      <a className="btn btn-primary btn-sm" href={bankIdSession.sameDeviceUrl}>
                        {t('milestoneAttestation.openBankId')}
                      </a>
                    )}
                  </div>
                )}

                {bankIdStatus === 'approved' && (
                  <div className="text-center py-4" role="status" aria-live="polite">
                    <div className="spinner-border text-success mb-3" aria-hidden="true"></div>
                    <h6 className="text-success">
                      {pendingAction === 'rejected' ? t('milestoneAttestation.savingDispute') : t('milestoneAttestation.bankIdApproved')}
                    </h6>
                    <p className="text-muted mb-0">
                      {pendingAction === 'rejected' ? t('milestoneAttestation.updatingMilestoneStatus') : t('milestoneAttestation.savingAttestation')}
                    </p>
                  </div>
                )}

                {bankIdStatus === 'success' && (
                  <div className="text-center py-4" role="status" aria-live="polite">
                    {pendingAction === 'rejected' ? (
                      <i
                        className="feather-alert-triangle text-warning d-block mb-3"
                        style={{ fontSize: '4rem' }}
                        aria-hidden="true"
                      ></i>
                    ) : (
                      <div className="bankid-success-circle mb-3">
                        <i className="feather-check"></i>
                      </div>
                    )}
                    <h6 className={pendingAction === 'rejected' ? 'text-warning' : 'text-success'}>
                      {pendingAction === 'rejected'
                        ? t('milestoneAttestation.disputeRegistered')
                        : showDisputeResolutionCopy
                          ? isConflict || hasActiveConflict
                            ? t('milestoneAttestation.conflictResolved')
                            : t('milestoneAttestation.disputeWithdrawn')
                          : t('milestoneAttestation.attestationApproved')}
                    </h6>
                    <p className="text-muted mb-0">
                      {pendingAction === 'rejected'
                        ? t('milestoneAttestation.paymentPausedContinueDialog')
                        : showDisputeResolutionCopy
                          ? isConflict || hasActiveConflict
                            ? t('milestoneAttestation.bankIdSavedConflictResumed', { subject: subjectLower })
                            : t('milestoneAttestation.bankIdSavedApproved', { subject: subjectLower })
                          : t('milestoneAttestation.bankIdAndDecisionSaved')}
                    </p>
                    {attestationWarning && <div className="alert alert-warning mt-3 mb-0" role="alert">{attestationWarning}</div>}
                  </div>
                )}

                {bankIdStatus === 'cancelled' && (
                  <div className="text-center py-4" role="status" aria-live="polite">
                    <i className="feather-x-circle text-warning d-block mb-3" style={{ fontSize: '3rem' }}></i>
                    <h6>{pendingAction === 'rejected' ? t('milestoneAttestation.disputeCancelled') : t('milestoneAttestation.bankIdCancelled')}</h6>
                    <p className="text-muted mb-0">
                      {pendingAction === 'rejected'
                        ? t('milestoneAttestation.disputeNotSavedUnchanged', { subject: subjectLower })
                        : t('milestoneAttestation.noAttestationSavedUnchanged', { subject: subjectUnchanged })}
                    </p>
                  </div>
                )}

                {bankIdStatus === 'failed' && (
                  <div className="text-center py-4" role="alert">
                    <i className="feather-alert-circle text-danger d-block mb-3" style={{ fontSize: '3rem' }}></i>
                    <h6 className="text-danger">
                      {pendingAction === 'rejected'
                        ? t('milestoneAttestation.disputeCouldNotBeSaved')
                        : bankIdSession?.verified || bankIdSession?.status === 'complete'
                          ? t('milestoneAttestation.attestationCouldNotBeSaved')
                          : t('milestoneAttestation.bankIdFailed')}
                    </h6>
                    <p className="text-muted mb-0">
                      {bankIdError || t('milestoneAttestation.noAttestationTryAgain')}
                    </p>
                  </div>
                )}
              </div>
              <div className="modal-footer flex-nowrap">
                {bankIdStatus === 'idle' && (
                  <>
                    <button type="button" className="btn btn-light" onClick={dismissBankIdStep}>
                      {t('milestoneAttestation.cancel')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={pendingAction === 'rejected' && !canFormallyDispute}
                      data-testid={pendingAction === 'rejected' ? 'submit-dispute' : undefined}
                      onClick={() =>
                        pendingAction === 'rejected'
                          ? void submitDispute()
                          : void startBankIdAttestation(pendingAction)
                      }
                    >
                      <i className={`feather-${pendingAction === 'rejected' ? 'arrow-right' : 'shield'} me-1`}></i>
                      {pendingAction === 'rejected' ? t('common.continue') : t('milestoneAttestation.continueWithBankId')}
                    </button>
                  </>
                )}
                {(bankIdStatus === 'starting' || bankIdStatus === 'waiting') && (
                  <button type="button" className="btn btn-outline-danger ms-auto" onClick={() => void cancelBankIdAttestation()}>
                    {t('milestoneAttestation.cancelBankId')}
                  </button>
                )}
                {bankIdStatus === 'success' && (
                  <button type="button" className="btn btn-primary ms-auto" onClick={handleClose}>
                    {t('milestoneAttestation.close')}
                  </button>
                )}
                {(bankIdStatus === 'cancelled' || bankIdStatus === 'failed') && (
                  <>
                    <button type="button" className="btn btn-light" onClick={dismissBankIdStep}>
                      {t('milestoneAttestation.back')}
                    </button>
                    <button type="button" className="btn btn-primary" onClick={retryBankIdAttestation}>
                      {t('milestoneAttestation.tryAgain')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
